import { diagnostic } from '../../utils/diagnostics';
import React, {
  useEffect,
  useState
} from 'react';

import {
  useNavigate
} from 'react-router-dom';

import {
  supabase
} from '../../services/supabase';

import {
  PASSWORD_RECOVERY_SESSION_KEY
} from '../../contexts/AuthContext';

import AppLayout from '../../components/Layout/ParentAccountLayout';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import MyPiggyIcon from '../../components/UI/MyPiggyIcon';

const MIN_PASSWORD_LENGTH = 8;

const ResetPassword = () => {
  const navigate =
    useNavigate();

  const [
    checkingSession,
    setCheckingSession
  ] = useState(true);

  const [
    hasRecoverySession,
    setHasRecoverySession
  ] = useState(false);

  const [
    password,
    setPassword
  ] = useState('');

  const [
    confirmPassword,
    setConfirmPassword
  ] = useState('');

  const [
    loading,
    setLoading
  ] = useState(false);

  const [
    error,
    setError
  ] = useState('');

  const [
    success,
    setSuccess
  ] = useState(false);

  /*
   * ---------------------------------------------------------
   * CHECK FOR A REAL SUPABASE PASSWORD-RECOVERY SESSION
   * ---------------------------------------------------------
   *
   * A normal authenticated parent session must NOT be enough
   * to unlock this page.
   *
   * AuthContext records PASSWORD_RECOVERY in sessionStorage
   * when Supabase processes a legitimate recovery link.
   */
  useEffect(() => {
    let mounted = true;

    const recoveryMarkerExists =
      () => {
        try {
          return (
            window.sessionStorage
              .getItem(
                PASSWORD_RECOVERY_SESSION_KEY
              ) === 'true'
          );
        } catch (
          storageError
        ) {
          diagnostic('error', 'parent.recovery.marker_read_failed');

          return false;
        }
      };

    const checkSession =
      async () => {
        try {
          const {
            data: {
              session
            },
            error:
              sessionError
          } =
            await supabase.auth
              .getSession();

          if (!mounted) {
            return;
          }

          if (sessionError) {
            diagnostic('error', 'parent.recovery.session_check_failed');
          }

          const validRecovery =
            Boolean(session) &&
            recoveryMarkerExists();

          setHasRecoverySession(
            validRecovery
          );
        } catch (
          sessionCheckError
        ) {
          diagnostic('error', 'parent.recovery.session_check_failed');

          if (mounted) {
            setHasRecoverySession(
              false
            );
          }
        } finally {
          if (mounted) {
            setCheckingSession(
              false
            );
          }
        }
      };

    checkSession();

    /*
     * Also listen directly in case the PASSWORD_RECOVERY event
     * occurs after this component mounts.
     */
    const {
      data: {
        subscription
      }
    } =
      supabase.auth
        .onAuthStateChange(
          (
            event,
            session
          ) => {
            if (!mounted) {
              return;
            }

            if (
              event !==
              'PASSWORD_RECOVERY'
            ) {
              return;
            }

            try {
              window.sessionStorage
                .setItem(
                  PASSWORD_RECOVERY_SESSION_KEY,
                  'true'
                );
            } catch (
              storageError
            ) {
              diagnostic('error', 'parent.recovery.marker_write_failed');
            }

            setHasRecoverySession(
              Boolean(session)
            );

            setCheckingSession(
              false
            );

            setError('');
          }
        );

    return () => {
      mounted = false;

      subscription
        ?.unsubscribe?.();
    };
  }, []);

  /*
   * ---------------------------------------------------------
   * CLEAR RECOVERY MARKER
   * ---------------------------------------------------------
   */
  const clearRecoveryMarker =
    () => {
      try {
        window.sessionStorage
          .removeItem(
            PASSWORD_RECOVERY_SESSION_KEY
          );
      } catch (
        storageError
      ) {
        diagnostic('error', 'parent.recovery.marker_cleanup_failed');
      }
    };

  /*
   * ---------------------------------------------------------
   * UPDATE PASSWORD
   * ---------------------------------------------------------
   */
  const handleSubmit =
    async (e) => {
      e.preventDefault();

      if (loading) {
        return;
      }

      setError('');

      if (
        password.length <
        MIN_PASSWORD_LENGTH
      ) {
        setError(
          `Your new password must be at least ${MIN_PASSWORD_LENGTH} characters long.`
        );

        return;
      }

      if (
        password !==
        confirmPassword
      ) {
        setError(
          'The two passwords do not match.'
        );

        return;
      }

      setLoading(true);

      try {
        /*
         * Re-check both the current Supabase session and the
         * recovery marker immediately before allowing the
         * password change.
         */
        const {
          data: {
            session
          },
          error:
            sessionError
        } =
          await supabase.auth
            .getSession();

        if (
          sessionError ||
          !session
        ) {
          throw new Error(
            'Password recovery session is no longer available.'
          );
        }

        let validRecovery =
          false;

        try {
          validRecovery =
            window.sessionStorage
              .getItem(
                PASSWORD_RECOVERY_SESSION_KEY
              ) === 'true';
        } catch (
          storageError
        ) {
          diagnostic('error', 'parent.recovery.marker_verify_failed');
        }

        if (!validRecovery) {
          throw new Error(
            'Password recovery session is not valid.'
          );
        }

        const {
          error:
            updateError
        } =
          await supabase.auth
            .updateUser({
              password
            });

        if (updateError) {
          throw updateError;
        }

        /*
         * The reset token has done its job. Clear our local
         * recovery marker before ending the Supabase session.
         */
        clearRecoveryMarker();

        /*
         * End the recovery session so the parent must explicitly
         * log in again using the new password.
         */
        const {
          error:
            signOutError
        } =
          await supabase.auth
            .signOut();

        if (signOutError) {
          diagnostic('error', 'parent.recovery.signout_failed');

          /*
           * Password change already succeeded, so don't tell the
           * user the password reset failed. The important part is
           * that the recovery marker has already been removed.
           */
        }

        setPassword('');
        setConfirmPassword('');

        setSuccess(true);

        setHasRecoverySession(
          false
        );
      } catch (
        submitError
      ) {
        diagnostic('error', 'parent.recovery.update_failed');

        const message =
          String(
            submitError
              ?.message ||
              ''
          ).toLowerCase();

        if (
          message.includes(
            'same password'
          ) ||
          message.includes(
            'different from the old password'
          )
        ) {
          setError(
            'Please choose a new password that is different from your current password.'
          );
        } else if (
          message.includes(
            'recovery session'
          )
        ) {
          clearRecoveryMarker();

          setHasRecoverySession(
            false
          );

          setError(
            'This password reset session is no longer valid. Please request a new reset link.'
          );
        } else if (
          message.includes(
            'weak'
          ) ||
          message.includes(
            'password'
          )
        ) {
          setError(
            submitError
              ?.message ||
              'That password cannot be used. Please choose a stronger password.'
          );
        } else {
          setError(
            'Unable to update your password. The reset link may have expired. Please request a new one.'
          );
        }
      } finally {
        setLoading(false);
      }
    };

  /*
   * ---------------------------------------------------------
   * CHECKING RECOVERY LINK
   * ---------------------------------------------------------
   */
  if (checkingSession) {
    return (
      <AppLayout title="MyPiggy">
        <div className="max-w-md mx-auto">
          <Card className="p-4 sm:p-5 text-center">
            <MyPiggyIcon name="my-piggy-bank" size="lg" className="mb-4" />

            <h2 className="text-xl sm:text-2xl font-extrabold text-gray-900 dark:text-slate-100 mb-2">
              Preparing password reset...
            </h2>

            <p className="text-gray-600 dark:text-slate-300">
              Please wait while we verify your reset link.
            </p>
          </Card>
        </div>
      </AppLayout>
    );
  }

  /*
   * ---------------------------------------------------------
   * SUCCESS SCREEN
   * ---------------------------------------------------------
   */
  if (success) {
    return (
      <AppLayout title="MyPiggy">
        <div className="max-w-md mx-auto">
          <Card className="p-4 sm:p-5">
            <div className="text-center">
              <MyPiggyIcon name="celebration-achievement" size="lg" className="mb-5" />

              <h2 className="text-2xl font-extrabold text-gray-900 dark:text-slate-100 mb-3">
                Password updated!
              </h2>

              <p className="text-gray-600 dark:text-slate-300 leading-relaxed mb-4">
                Your password has been changed successfully.
                You can now sign in using your new password.
              </p>

              <Button
                type="button"
                className="w-full"
                onClick={() =>
                  navigate(
                    '/parent/login',
                    {
                      replace: true
                    }
                  )
                }
              >
                Return to Parent Login
              </Button>
            </div>
          </Card>
        </div>
      </AppLayout>
    );
  }

  /*
   * ---------------------------------------------------------
   * INVALID / EXPIRED RECOVERY LINK
   * ---------------------------------------------------------
   */
  if (!hasRecoverySession) {
    return (
      <AppLayout title="MyPiggy">
        <div className="max-w-md mx-auto">
          <Card className="p-4 sm:p-5">
            <div className="text-center">
              <div
                className="text-6xl mb-5"
                aria-hidden="true"
              >
                ⏰
              </div>

              <h2 className="text-2xl font-extrabold text-gray-900 dark:text-slate-100 mb-3">
                Reset link unavailable
              </h2>

              <p className="text-gray-600 dark:text-slate-300 leading-relaxed mb-4">
                This password reset link is invalid or has expired.
                Please request a new password reset email.
              </p>

              <div className="space-y-3">
                <Button
                  type="button"
                  className="w-full"
                  onClick={() =>
                    navigate(
                      '/forgot-password',
                      {
                        replace: true
                      }
                    )
                  }
                >
                  Request a New Reset Link
                </Button>

                <button
                  type="button"
                  onClick={() =>
                    navigate(
                      '/parent/login',
                      {
                        replace: true
                      }
                    )
                  }
                  className="w-full text-blue-600 dark:text-blue-400 hover:text-blue-500 font-medium text-sm"
                >
                  Back to Parent Login
                </button>
              </div>
            </div>
          </Card>
        </div>
      </AppLayout>
    );
  }

  /*
   * ---------------------------------------------------------
   * NEW PASSWORD FORM
   * ---------------------------------------------------------
   */
  return (
    <AppLayout title="MyPiggy">
      <div className="max-w-md mx-auto">
        <Card className="p-4 sm:p-5">
          <div className="text-center mb-4">
            <div
              className="text-5xl mb-4"
              aria-hidden="true"
            >
              🔑
            </div>

            <h2 className="text-2xl font-extrabold text-gray-900 dark:text-slate-100 mb-2">
              Create a new password
            </h2>

            <p className="text-gray-600 dark:text-slate-300">
              Enter a new password for your MyPiggy parent account.
            </p>
          </div>

          <form
            onSubmit={handleSubmit}
            className="space-y-4"
          >
            <Input
              label="New Password"
              type="password"
              name="password"
              value={password}
              onChange={(e) => {
                setPassword(
                  e.target.value
                );

                if (error) {
                  setError('');
                }
              }}
              autoComplete="new-password"
              helperText={`Use at least ${MIN_PASSWORD_LENGTH} characters.`}
              required
              autoFocus
            />

            <Input
              label="Confirm New Password"
              type="password"
              name="confirmPassword"
              value={
                confirmPassword
              }
              onChange={(e) => {
                setConfirmPassword(
                  e.target.value
                );

                if (error) {
                  setError('');
                }
              }}
              autoComplete="new-password"
              required
            />

            {error && (
              <div
                className="text-red-700 dark:text-red-300 text-sm bg-red-50 dark:bg-red-950/35 border border-red-100 p-3 rounded-lg"
                role="alert"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              className="w-full"
              disabled={loading}
            >
              {loading
                ? 'Updating Password...'
                : 'Update Password'}
            </Button>
          </form>
        </Card>
      </div>
    </AppLayout>
  );
};

export default ResetPassword;

